import React from 'react'
import styled from 'styled-components'

const buttonSizes = {
    small: 'small',
    normal: 'normal',
    large: 'large'
}

const buttonVariants = {
    contained: 'contained'
}

const buttonColors = {
    primary: 'primary',
    secondary: 'secondary',
    tertiary: 'tertiary'
}

const Button = (props) => {
    const { onClick, children, size, color, variant, startIcon, type, disabled, autofocus, name, value } = props

    return (
    <StyledButton
        onClick={onClick}
        name={name}
        value={value}
        autoFocus={autofocus}
        disabled={disabled}
        type={type}
        size={size}
        color={color}
        variant={variant}
        {...props}
    >
        { startIcon && <span className='button__start-icon'>{startIcon}</span> }
        {children}
    </StyledButton>
    )
}

export const StyledButton = styled.button`
    align-items: center;
    outline: none;
    box-sizing: border-box;
    padding: 0 16px;
    line-height: 1.5;
    font-size: ${props => {
    switch (props.size) {
            case buttonSizes.small:
                return '12px'
            case buttonSizes.large:
                return '14px'
            default:
                return '14px'
        }
    }};

    font-family: ${props => props.theme.typography.fontFamily};
    font-weight: 700;

    height: ${props => {
        switch (props.size) {
            case buttonSizes.small:
            return props.theme.dimensions.buttonSmallHeight
            case buttonSizes.large:
            return props.theme.dimensions.buttonLargeHeight
            default:
            return props.theme.dimensions.buttonNormalHeight
        }
    }};

    color: ${props => {
        switch (true) {
            case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                return props.theme.palette.primary.contrastText
            case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                return props.theme.palette.secondary.contrastText
            case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                return props.theme.palette.tertiary.contrastText
            default:
                return props.theme.palette.text.main
        }
    }};

    background-color: ${props => {
        switch (true) {
            case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                return props.theme.palette.primary.main
            case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                return props.theme.palette.secondary.main
            case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                return props.theme.palette.tertiary.main
            default:
                return 'transparent'
        }
    }};

    border-width: ${props => props.color === buttonColors.tertiary ? '0px' : '1px'};
    border-style: ${props => props.color === buttonColors.tertiary ? 'none' : 'solid'};
    border-color: transparent;

    background-position: center;

    &:disabled {
        color: ${props => {
            switch (true) {
                case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                    return props.theme.palette.primary.contrastText
                case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                    return props.theme.palette.common.neutral700
                case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                    return props.theme.palette.common.neutral500
                default:
                    return props.theme.palette.text.main
            }
        }};

        opacity: ${props => {
            switch (true) {
                case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                    return 0.5
                default:
                    return 1
            }
        }};

        background-color: ${props => {
            switch (true) {
                case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                    return props.theme.palette.primary.main
                case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                    return props.theme.palette.secondary.light
                case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                    return props.theme.palette.tertiary.light
                default:
                    return 'transparent'
            }
        }};
    }
  
    &:hover:enabled {
        cursor: pointer;
        outline: none;
        background: ${props => {
        switch (true) {
            case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                return `${props.theme.palette.primary.dark}`
            case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                return `${props.theme.palette.secondary.dark}`
            case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                return `${props.theme.palette.tertiary.dark}`
            default:
                return 'transparent'
            }
        }};

        border-color: transparent;
    }

    &:active:enabled {
        outline: none;
        background-color: ${props => {
            switch (true) {
            case props.variant === buttonVariants.contained && props.color === buttonColors.primary:
                return props.theme.palette.common.red700
            case props.variant === buttonVariants.contained && props.color === buttonColors.secondary:
                return props.theme.palette.common.neutral700
            case props.variant === buttonVariants.contained && props.color === buttonColors.tertiary:
                return props.theme.palette.common.neutral300
            default:
                return 'transparent'
            }
        }};

        border-color: 'transparent';
        background-size: 100%;
    }
  
    .button__start-icon {
        margin-right: 8px;
    }
`

Button.defaultProps = {
    disabled: false,
    type: 'button',
    size: buttonSizes.normal,
    variant: buttonVariants.contained,
    color: buttonColors.primary
}

export default Button
